import { useEffect, useRef } from 'react';
import { tagTones } from '../design/tagPalette';
import type { TagColorId } from '../design/tagPalette';
import type { Theme } from '../design/tokens';
import type { Folder } from '../entity/folders';
import { folderMark, TagMarkGlyph } from '../folders/TagMark';

/**
 * A nook's colour, chosen from the nook: choosing a colour is choosing its
 * tag. The tag's mark beside the name — its icon, or the folder glyph — opens a list
 * of the folders it can wear, each by its own mark, and a way to wear none.
 * A tag of its own, minted with a colour here, needs the vault side to mint
 * one, and is absent until it can.
 */
export function NookTagPicker({
  tag,
  hue,
  folders,
  theme,
  open,
  onOpen,
  onClose,
  onChoose,
}: {
  /** The tag the nook carries, or null. */
  tag: string | null;
  /** That tag's colour, or null where it has none or the nook carries none. */
  hue: TagColorId | null;
  folders: readonly Folder[];
  theme: Theme;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** A folder's name to wear, or null for no colour. */
  onChoose: (tag: string | null) => void;
}) {
  const box = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) onClose();
    };
    // Deferred, so the press that opened it does not also close it.
    const id = setTimeout(() => document.addEventListener('pointerdown', away));
    return () => {
      clearTimeout(id);
      document.removeEventListener('pointerdown', away);
    };
  }, [open, onClose]);

  const wearable = folders.filter((f) => f.hue !== null);
  const current = tag?.toLowerCase() ?? null;
  const worn = current ? folders.find((f) => f.name.toLowerCase() === current) : undefined;

  return (
    <span className="nook-tag" ref={box}>
      <button
        type="button"
        className="nook-btn nook-icon nook-tag-button"
        aria-label={`This nook's tag: ${tag ?? 'none'}`}
        aria-expanded={open}
        onClick={() => (open ? onClose() : onOpen())}
      >
        {worn ? (
          <TagMarkGlyph mark={folderMark(worn, theme)} size={16} named />
        ) : (
          <span
            className={`nook-swatch nook-tag-swatch${hue ? '' : ' is-none'}`}
            style={hue ? { background: tagTones(theme, hue).mark } : undefined}
          />
        )}
      </button>
      {open && (
        <div className="nook-tag-pop" role="dialog" aria-label="This nook's colour">
          <p className="nook-tag-label">Wear a tag's</p>
          <div className="nook-tag-rows">
            {wearable.map((folder) => (
              <button
                key={folder.id}
                type="button"
                className="nook-tag-row"
                aria-pressed={folder.name.toLowerCase() === current}
                onClick={() => onChoose(folder.name)}
              >
                <span className="nook-tag-mark">
                  <TagMarkGlyph mark={folderMark(folder, theme)} size={14} named />
                </span>
                <span className="nook-tag-name">{folder.name}</span>
                {folder.colorFrom && <span className="nook-tag-from">· from {folder.colorFrom}</span>}
              </button>
            ))}
          </div>
          <button type="button" className="nook-tag-none" onClick={() => onChoose(null)}>
            No colour
          </button>
        </div>
      )}
    </span>
  );
}
